import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { IFilterButtonGroupItems } from '@/bundles/GroupFilters/interfaces/inputs/IFilterButtonGroupItems';
import { AllExcludeOnlyLabels } from '@/bundles/GroupFilters/enums/AllExcludeOnlyEnum';
import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import { Nullable } from '@/utils/types';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import { getOccupancyTypeFilterItems } from '@/bundles/Occupier/helpers/getOccupancyTypeFilterItems';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import i18n from '@/bundles/Localization';
import { RentScheduleTypeEnum } from '@/bundles/Comparables/types';
import { getContactRolesBySource } from '@/bundles/Contact/helpers/cards/contactsCard';
import {
  companyRoles,
  constructionStatuses,
  FilterPrimaryUseClassItems,
  getCountriesForFilters,
  getMarketsFilters,
  getPropertyTypesWithMixedUseForFilters,
  getPropertyUsesForFilters,
  PROPERTY_TYPE_KEY,
} from '@/bundles/GroupFilters/consts';
import { getRailsStatusItems } from '@/bundles/Property/helpers';
import { FilterOfficeEnterpriseItems } from '@/bundles/GroupFilters/enums/FilterOfficeEnterpriseEnum';
import { getIndustryTypes, getIndustryTypesByGroup } from '@/bundles/Companies/types/companyIndustryType';
import { FilterBrokerageItems } from '@/bundles/GroupFilters/enums/FilterBrokerageEnum';
import { FilterPrimaryUseClassEnum, OccupierSpatialSearchRecordTypeEnum } from '@/bundles/GroupFilters/types';
import { IGroupFilterItem } from '@/bundles/GroupFilters/helpers';
import { occupierTplTypeLabels } from '@/bundles/Occupier/consts';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';

export const getOccupierFilters = (filters?: Nullable<IGroupFilterItem>): IFilterConfigItem[] => {
  const propertyTypesWithMixedUse = getPropertyTypesWithMixedUseForFilters();
  const selectedPropertyTypes = filters?.[PROPERTY_TYPE_KEY]?.value || [];

  return [
    // FilterCategoryEnum.occupier_details start
    {
      title: 'Vacated Occupiers',
      name: 'occupier_with_vacated',
      items: <IFilterButtonGroupItems>Object.keys(AllExcludeOnlyLabels).map((key) => ({
        text: AllExcludeOnlyLabels[key],
        value: key,
      })),
      type: FilterTypeEnum.button_group,
      category: FilterCategoryEnum.occupier_details,
      additional: {
        mandatory: false,
      }
    },
    {
      title: 'Occupier Space Size',
      name: 'occupier_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_details,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Land Size',
      name: 'occupier_land_size', // sending with occupier_land_size_unit
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Occupancy Type',
      name: 'occupier_occupancy_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getOccupancyTypeFilterItems(),
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Occupier Use Type(s)',
      name: 'occupier_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: i18n.t(LocalizationEnum.schedule_type) as string,
      name: 'occupier_schedule_type',
      type: FilterTypeEnum.one_select,
      items: [
        { text: RentScheduleTypeEnum.new, value: RentScheduleTypeEnum.new },
        { text: RentScheduleTypeEnum.renewal, value: RentScheduleTypeEnum.renewal },
        { text: RentScheduleTypeEnum.modification, value: RentScheduleTypeEnum.modification },
        { text: RentScheduleTypeEnum.expansion, value: RentScheduleTypeEnum.expansion },
        { text: RentScheduleTypeEnum.extension, value: RentScheduleTypeEnum.extension },
      ],
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Sublease',
      name: 'occupier_sublease',
      type: FilterTypeEnum.switch,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Lease Expiration Date',
      name: 'occupier_lease_expiration_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Lease Expiration in',
      name: 'occupier_lease_expiration',
      type: FilterTypeEnum.one_select,
      items: [
        { text: '3 months', value: '3 months' },
        { text: '6 months', value: '6 months' },
        { text: '9 months', value: '9 months' },
        { text: '12 months', value: '12 months' },
        { text: '18 months', value: '18 months' },
        { text: '21 months', value: '21 months' },
        { text: '24 months', value: '24 months' },
      ],
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Apply spatial search for occupiers',
      name: 'occupier_polygon_switch',
      type: FilterTypeEnum.switch,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Original Possession Date',
      name: 'occupier_original_possession_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Lease Commencement Date',
      name: 'occupier_lease_commencement',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Lease Execution Date',
      name: 'occupier_lease_execution',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Lease Term (months)',
      name: 'occupier_lease_term',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Free Rent',
      name: 'occupier_free_rent',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Starting Base Rent / SF',
      name: 'occupier_starting_rent',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Average Rent',
      name: 'occupier_average_rent',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Effective Rent',
      name: 'occupier_effective_rent',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: '3PL Type',
      name: 'occupier_tpl_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: Object.keys(occupierTplTypeLabels).map((value) => ({
        text: occupierTplTypeLabels[value],
        value,
      })),
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: '3PL Exists',
      name: 'occupier_tpl_exists',
      type: FilterTypeEnum.switch,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Inventory Amount PSF',
      name: 'occupier_inventory_per_sf',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.occupier_details,
    },
    {
      title: 'Inventory Amount Total',
      name: 'occupier_inventory_total',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.occupier_details,
    },
    // FilterCategoryEnum.occupier_details end
    // FilterCategoryEnum.company_filters start
    {
      title: 'Company Type',
      name: 'company_my_office_enterprise',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterOfficeEnterpriseItems,
      category: FilterCategoryEnum.company_filters,
      additional: {
        mandatory: false,
      }
    },
    {
      title: 'Company Name',
      name: 'company_name',
      type: FilterTypeEnum.company_name_combobox,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'City',
      name: 'company_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'State',
      name: 'company_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Country',
      name: 'company_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Postal',
      name: 'company_postal',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Industry Group',
      name: 'company_industry_group',
      items: getIndustryTypes(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Industry',
      name: 'company_industry',
      items: getIndustryTypesByGroup(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Annual Revenue',
      name: 'company_annual_revenue',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Is Brokerage',
      name: 'company_is_brokerage',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterBrokerageItems,
      category: FilterCategoryEnum.company_filters,
      additional: {
        mandatory: false
      }
    },
    // FilterCategoryEnum.company_filters start
    // FilterCategoryEnum.contact_filters start
    {
      title: 'Contact Role',
      name: 'occupier_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('occupier'),
      category: FilterCategoryEnum.contact_filters,
    },
    // FilterCategoryEnum.contact_filters end
    // FilterCategoryEnum.property_filters start
    {
      title: 'Property Building Size',
      name: 'property_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Property Land Size',
      name: 'property_land_size', // sending with property_land_size_unit
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.property_filters,
      children: [
        {
          title: 'Property Type',
          name: PROPERTY_TYPE_KEY,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Property Type As Primary Use',
          name: 'property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: 'Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.property_filters,
      children: [
        {
          title: 'Property Use Class',
          name: 'property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Property Use',
          name: 'property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Property Use Type(s)',
      name: 'property_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Rail Status',
      name: 'property_rail_status',
      items: getRailsStatusItems(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Construction Status',
      items: constructionStatuses,
      name: 'property_construction_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Company Role',
      items: companyRoles,
      name: 'property_company_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    // FilterCategoryEnum.property_filters end
    // FilterCategoryEnum.polygon_search start
    {
      title: 'Search by',
      name: 'occupier_spatial_search_record_type',
      type: FilterTypeEnum.one_select,
      items: [
        { text: 'Occupier Location', value: OccupierSpatialSearchRecordTypeEnum.occupier },
        { text: 'Company Location', value: OccupierSpatialSearchRecordTypeEnum.company },
      ],
      category: FilterCategoryEnum.polygon_search,
    },
    // FilterCategoryEnum.polygon_search end

    // FilterCategoryEnum.market_location start
    {
      title: 'Address',
      name: 'property_address',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'City',
      name: 'property_location_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.market_location,
      additional: {
        citiesSource: 'property',
      }
    },
    {
      title: 'State',
      name: 'property_location_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Country',
      name: 'property_location_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Zip / Postal Code',
      name: 'property_location_postal_cd',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.market_location,
    },
    ...getMarketsFilters({ propertyTypes: selectedPropertyTypes }),
    {
      title: `Property ${i18n.t(LocalizationEnum.apn_number)}`,
      name: 'property_apn_number',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    // FilterCategoryEnum.market_location end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Edited Date',
      name: 'occupier_view_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'occupier_created_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'occupier_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'occupier_view',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'occupier_created_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'occupier_edited_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('occupier'),
  ].filter(Boolean) as IFilterConfigItem[];
};
